import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import { AnyAction } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'
import { CreateApiOptions } from '@reduxjs/toolkit/dist/query/createApi'
import { FetchBaseQueryArgs } from '@reduxjs/toolkit/dist/query/fetchBaseQuery'

import cognito from './cognito'
import { debounce } from 'lodash'
import { Project } from './project'
import { getStrings } from './strings'
import { REHYDRATE } from 'redux-persist'
import { Constants } from './utils/constants'
import { getStore, StoreStateType } from './store'
import { getApi } from './api-common'

export function extractRehydrationInfo(
  action: AnyAction,
  {
    reducerPath,
  }: {
    reducerPath: string
  },
) {
  if (action.type === HYDRATE) {
    return action.payload[reducerPath]
  }
}

let q: BaseQueryFn<any> | null = null

const debouncedLogout = debounce(
  () => {
    getStore().dispatch(service.endpoints.logout.initiate({}, {}))
    // logout(getStore(), {})
  },
  500,
  { leading: true },
)
export const baseApiOptions = (queryArgs?: Partial<FetchBaseQueryArgs>) => {
  const baseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
  > = async (args, api, extraOptions) => {
    const _baseQuery = q
      ? q
      : fetchBaseQuery({
          baseUrl:
            typeof USE_PROXY !== 'undefined' && USE_PROXY
              ? '/api/proxy'
              : Project.api,
          fetchFn: async (args) => {
            if (typeof USE_PROXY !== 'undefined' && USE_PROXY) {
              args.headers.append('originalurl', args.url)
            }
            const result = await fetch(args)
            const isUnauthorised = result?.status === 401
            const isConfirmEmail = args.url.includes('change-email-confirm')

            if (isUnauthorised && !isConfirmEmail) {
              debouncedLogout()
            }
            return result
          },
          prepareHeaders: async (headers, args) => {
            const { endpoint, getState } = args
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const state = getState() as StoreStateType

            Object.entries(Project.headers || {}).forEach(([key, value]) => {
              headers.set(key, value as string)
            })

            if (
              endpoint !== 'register' &&
              endpoint !== 'createConfirmEmail' &&
              endpoint !== 'createResetPassword' &&
              endpoint !== 'createResendConfirmationEmail' &&
              endpoint !== 'verifyAccount' &&
              endpoint !== 'createForgotPassword'
            ) {
              try {
                const session = await cognito.getSession()
                const jwtToken = await session!.getAccessToken()
                const testData = state?.devSettings?.testData
                if (testData) {
                  headers.set('x-is-test-request', `True`)
                }
                if (jwtToken?.getJwtToken()) {
                  headers.set(
                    'Authorization',
                    `Bearer ${jwtToken?.getJwtToken()}`,
                  )
                }
              } catch (e) {}
            }

            const alternativeToken = getApi()?.getToken?.()
            if (alternativeToken) {
              headers.set('Authorization', `Bearer ${alternativeToken}`)
            }
            if (typeof USE_PROXY !== 'undefined' && USE_PROXY) {
              headers.set('url-original', endpoint)
            }
            headers.set('Accept-Language', getStrings().getLanguage())
            if (Constants.E2E) {
              headers.set('E2E-Test', '1')
            }
            return headers
          },
          ...queryArgs,
        })
    q = _baseQuery
    return _baseQuery(args, api, extraOptions)
  }

  const res: Pick<
    CreateApiOptions<any, any, any, any>,
    | 'baseQuery'
    | 'keepUnusedDataFor'
    | 'refetchOnReconnect'
    | 'refetchOnFocus'
    | 'extractRehydrationInfo'
  > = {
    baseQuery,
    extractRehydrationInfo: (action, { reducerPath }) => {
      if (action.type === HYDRATE) {
        return action.payload[reducerPath]
      }
    },

    refetchOnFocus: true,
    refetchOnReconnect: true,
  }
  return res
}

const transformQuery = (query: any | undefined | null) => {
  // @ts-ignore
  if (!query?.data) {
    // if the query had data, treat it as cache
    return null
  }
  return {
    ...query,
    // Preserve timestamp for offline cache or set a recent one to keep data valid
    fulfilledTimeStamp: query.fulfilledTimeStamp || Date.now(),
    status: 'fulfilled',
  }
}
export const service = createApi({
  ...baseApiOptions(),
  endpoints: () => ({}),
  // @ts-ignore
  extractRehydrationInfo(action, { reducerPath }) {
    if (
      (action.type === REHYDRATE ||
        action.type === '__NEXT_REDUX_WRAPPER_HYDRATE__') &&
      action.payload?.service
    ) {
      const queries = {}
      Object.keys(action.payload?.service?.queries).map((key) => {
        // @ts-ignore
        queries[key] = transformQuery(action.payload?.service?.queries[key])
      })
      return {
        'config': {},
        'mutations': {},
        'provided': {},
        'queries': queries,
        'subscriptions': {},
      }
    }
  },
  keepUnusedDataFor: 10,
  reducerPath: 'service',
})
